import { useState } from 'react';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { bounceIn, grid } from '@shared/styles';
import { useMountedRef } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { COPY_TOOLTIP_DURATION_MS } from './constants';

interface UrlActionButtonsProps {
  url: string;
  showCopy?: boolean;
  showOpen?: boolean;
}

export function UrlActionButtons({ url, showCopy = true, showOpen = true }: UrlActionButtonsProps) {
  const mounted = useMountedRef();
  const initialCopyTooltip = i18n('workflowTemplate.component.url.actionButtons.copyToClipboard');
  const copiedTooltip = i18n('workflowTemplate.component.url.actionButtons.copiedToClipboard');
  const [copyTooltip, setCopyTooltip] = useState(initialCopyTooltip);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyTooltip(copiedTooltip);
      setTimeout(() => {
        if (mounted.current) {
          setCopyTooltip(initialCopyTooltip);
        }
      }, COPY_TOOLTIP_DURATION_MS);
    } catch (err) {
      // Fallback for browsers that don't support navigator.clipboard
      console.error('Failed to copy to clipboard', err);
    }
  };

  const openUrl = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const shouldRender = showCopy || showOpen;
  if (!shouldRender) {
    return null;
  }

  return (
    <View row centerVertically gap={1}>
      {showCopy && (
        <AnchoredTooltip tooltip={copyTooltip}>
          <Button
            variant="tertiary"
            size="small"
            icon="squares-stacked"
            iconOnly
            iconSize={grid(2.5)}
            onClick={copyToClipboard}
            className={bounceIn}
          />
        </AnchoredTooltip>
      )}
      {showOpen && (
        <AnchoredTooltip
          tooltip={i18n('workflowTemplate.component.url.actionButtons.openUrl', { url })}
        >
          <Button
            variant="tertiary"
            size="small"
            icon="arrow-link-out"
            iconOnly
            iconSize={grid(2.25)}
            onClick={openUrl}
            className={bounceIn}
          />
        </AnchoredTooltip>
      )}
    </View>
  );
}
